import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import LoadingScreen from "../screens/loading-screen";
import ErrorScreen from "../screens/error-screen";
import {
	Box,
	Button,
	Fab,
	Grid,
	Pagination,
	Paper,
	TextField,
	Typography,
	InputAdornment,
	Tooltip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Avatar,
	Chip,
	Card,
	Stack,
	useTheme,
	alpha,
	CircularProgress,
	Slide,
	IconButton,
	Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CircleIcon from "@mui/icons-material/Circle";
import KeyIcon from "@mui/icons-material/Key";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockIcon from "@mui/icons-material/Lock";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import BuildIcon from "@mui/icons-material/Build";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import { styled, keyframes } from "@mui/material/styles";
import isMobile from "is-mobile";
import { DELETE_TECHNICIAN, EDIT_TECHNICIAN_PASSWORD } from "../graphql/mutation/technician";
import { UpdateClients } from "../graphql/mutation/updateClients";
import { getFieldsRead } from "../helpers";
import { useTranslation } from "react-i18next";

// Define animations for fadeIn and pulsate
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulsate = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Styled components for better UI
const FadeInGridItem = styled(Grid)(({ delay = 0 }) => ({
	opacity: 0,
	animation: `${fadeIn} 0.6s ease-out forwards`,
	animationDelay: `${delay}s`,
}));

const PulsateCircle = styled(Box)(({ color }) => ({
	width: 10,
	height: 10,
	borderRadius: '50%',
	backgroundColor: color,
	boxShadow: `0 0 6px ${color}`,
	animation: `${pulsate} 2s infinite ease-in-out`,
}));

const FloatingFab = styled(Fab)(({ theme }) => ({
	position: "fixed",
	bottom: 30,
	right: 30,
	animation: `${float} 3s infinite ease-in-out`,
	background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
	boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
	'&:hover': {
		background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
		boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
		transform: 'translateY(-2px)',
	},
	transition: 'all 0.3s ease',
}));

const StyledCard = styled(Card)(({ theme, isRented }) => ({
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	borderRadius: theme.shape.borderRadius * 2,
	transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
	position: 'relative',
	overflow: 'hidden',
	backgroundColor: theme.palette.background.paper,
	boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}`,
	border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
	'&:hover': {
		transform: 'translateY(-8px)',
		boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.12)}`,
	},
	...(isRented && {
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			right: 0,
			width: '100%',
			height: '4px',
			background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
		},
	}),
}));

const formatDisplayValue = (value) => {
	if (value === null || value === undefined || value === "") {
		return "-";
	}

	if (typeof value !== "object") {
		return String(value);
	}

	if (Array.isArray(value)) {
		const formattedItems = value
			.map((item) => formatDisplayValue(item))
			.filter((item) => item && item !== "-");
		return formattedItems.length ? formattedItems.join(", ") : "-";
	}

	const knownValue =
		value.name ||
		value.label ||
		value.address ||
		value.Address ||
		value.postalCode ||
		value.localidade;

	if (knownValue) {
		return String(knownValue);
	}

	const seen = new WeakSet();
	try {
		return JSON.stringify(value, (_key, nestedValue) => {
			if (nestedValue && typeof nestedValue === "object") {
				if (seen.has(nestedValue)) {
					return "[Circular]";
				}
				seen.add(nestedValue);
			}
			return nestedValue;
		});
	} catch (_error) {
		return "-";
	}
};

const Viewer = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [term, setTerm] = useState("");
	const [value, setValue] = useState("");
	const [limit, setLimit] = useState(9);
	const [offSet, setOffSet] = useState(0);
	const [selectedTechnician, setSelectedTechnician] = useState(null);
	const [newPassword, setNewPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [currentItemId, setCurrentItemId] = useState(null);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [clientEditOpen, setClientEditOpen] = useState(false);
	const [clientForm, setClientForm] = useState({
		_id: "",
		name: "",
		email: "",
		phoneNumber: "",
		nif: "",
		address: "",
		postalCode: "",
		localidade: "",
	});

	const { viewerInfo, viewerQueryAll, detailPathBuilder } = props;
	const navigate = useNavigate();

	const { loading, error, data, refetch } = useQuery(viewerQueryAll, {
		variables: { term, limit, offSet },
	});

	const [deleteTechnician] = useMutation(DELETE_TECHNICIAN);
	const [editTechnicianPassword] = useMutation(EDIT_TECHNICIAN_PASSWORD);
	const [updateClient] = useMutation(UpdateClients);

	// Handle scroll position for visual effects
	useEffect(() => {
		const handleScroll = () => {
			setScrollPosition(window.scrollY);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const handleChangePage = (event, page) => {
		setOffSet(page - 1);
		// Scroll to top when page changes
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	useEffect(() => {
		refetch();
	}, [term, refetch]);

	const toUse = data && data[viewerInfo];
	const fields = getFieldsRead(viewerInfo);

	if (error) return <ErrorScreen error={error} onRetry={() => refetch()} />;

	let timer = null;

	const handleDeleteTechnician = (id) => {
		setIsSubmitting(true);
		deleteTechnician({ variables: { id } })
			.then(() => {
				refetch();
				setConfirmDelete(false);
				setCurrentItemId(null);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const handleEditPassword = () => {
		if (!newPassword.trim()) {
			return;
		}

		setIsSubmitting(true);
		editTechnicianPassword({
			variables: { technicianId: selectedTechnician, newPassword },
		})
			.then(() => {
				setSelectedTechnician(null);
				setNewPassword("");
				refetch();
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const openClientEditDialog = (item) => {
		setClientForm({
			_id: item._id,
			name: item.name || "",
			email: item.email || "",
			phoneNumber: item.phoneNumber || "",
			nif: item.nif || "",
			address: item.addressInfo?.Address || "",
			postalCode: item.addressInfo?.postalCode || "",
			localidade: item.localidade || "",
		});
		setClientEditOpen(true);
	};

	const handleSaveClient = () => {
		setIsSubmitting(true);
		updateClient({
			variables: {
				id: clientForm._id,
				name: clientForm.name,
				email: clientForm.email,
				phoneNumber: clientForm.phoneNumber,
				nif: clientForm.nif,
				address: clientForm.address,
				postalCode: clientForm.postalCode,
				localidade: clientForm.localidade,
			},
		})
			.then(() => {
				setClientEditOpen(false);
				refetch();
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const getIconForViewer = () => {
		switch (viewerInfo) {
			case 'machines':
				return <InventoryIcon />;
			case 'technicians':
				return <BuildIcon />;
			case 'clients':
				return <PeopleIcon />;
			default:
				return <InventoryIcon />;
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'RENTED':
				return theme.palette.warning.main;
			case 'ACTIVE':
				return theme.palette.success.main;
			case 'INACTIVE':
				return theme.palette.error.main;
			default:
				return theme.palette.success.main;
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case 'RENTED':
				return t("STATUS.IN_USE");
			case 'ACTIVE':
				return t("STATUS.ACTIVE");
			case 'INACTIVE':
				return t("STATUS.INACTIVE");
			default:
				return t("STATUS.AVAILABLE");
		}
	};

	return (
		<Container maxWidth="xl" sx={{ pb: 8, pt: 2 }}>
			{/* Search Bar */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					width: '100%',
					mb: 4,
					mt: 2,
					position: 'relative',
					zIndex: 2,
				}}
			>
				<Paper
					elevation={scrollPosition > 50 ? 3 : 1}
					sx={{
						p: 2.5,
						borderRadius: 3,
						width: { xs: '95%', sm: '80%', md: '70%' },
						maxWidth: '900px',
						backgroundColor: alpha(theme.palette.background.paper, 0.9),
						backdropFilter: 'blur(8px)',
						transition: 'all 0.3s ease',
						transform: scrollPosition > 50 ? 'translateY(8px)' : 'translateY(0)',
					}}
				>
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						spacing={2}
						alignItems="center"
					>
						<TextField
							placeholder={t("COMMON.SEARCH_ENTITY", { entity: t(`PATH.${viewerInfo}`) })}
							value={value}
							variant="outlined"
							fullWidth
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon color="action" />
									</InputAdornment>
								),
								endAdornment: value && (
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={() => {
												setValue('');
												setTerm('');
											}}
										>
											<RefreshIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
								sx: {
									borderRadius: 2,
									bgcolor: '#ffffff',
									'&.Mui-focused': {
										boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
									},
								}
							}}
							onChange={(event) => {
								clearTimeout(timer);
								const newTerm = event.target.value;
								setValue(newTerm);
								timer = setTimeout(() => {
									setTerm(newTerm);
								}, 500);
							}}
							sx={{
								backgroundColor: "#ffffff",
								borderRadius: 2,
							}}
						/>

						{!isMobile() && (
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={() => navigate(`/create?type=${viewerInfo}`)}
								sx={{
									borderRadius: 2,
									py: 1.5,
									px: 3,
									fontWeight: 600,
									minWidth: 140,
									background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
									boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
									'&:hover': {
										boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
									},
								}}
							>
								{t("ACTIONS.ADD")}
							</Button>
						)}
					</Stack>
				</Paper>
			</Box>

			{/* Page Title */}
			<Box sx={{ mb: 4, px: 2 }}>
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					sx={{
						mb: 1,
						opacity: 0,
						animation: `${fadeIn} 0.5s forwards`,
					}}
				>
					<Avatar
						sx={{
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							color: theme.palette.primary.main,
							width: 44,
							height: 44,
						}}
					>
						{getIconForViewer()}
					</Avatar>
					<Typography variant="h4" fontWeight={700} color="text.primary">
						{t(`PATH.${viewerInfo}`)}
					</Typography>
				</Stack>

				{!loading && toUse && (
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{
							ml: 7,
							opacity: 0,
							animation: `${fadeIn} 0.5s 0.2s forwards`,
						}}
					>
						{t("COMMON.FOUND_COUNT", {
							count: toUse.paginantionInfo?.numberDocuments || 0,
							entity: t(`PATH.${viewerInfo}`),
						})}
						{term && <> {t("COMMON.CONTAINING", { term })}</>}
					</Typography>
				)}
			</Box>

			{/* Loading State */}
			{loading ? (
				<LoadingScreen />
			) : (
				<>
					{/* Floating Action Button for Mobile */}
					{isMobile() && (
						<FloatingFab
							onClick={() => navigate(`/create?type=${viewerInfo}`)}
							color="primary"
							aria-label={t("ACTIONS.ADD")}
							size="large"
						>
							<AddIcon fontSize="medium" />
						</FloatingFab>
					)}

					{/* Items Grid */}
					<Grid
						container
						spacing={3}
						justifyContent="center"
						sx={{
							px: { xs: 2, sm: 2, md: 2 },
							mb: { xs: 10, sm: 4, md: 4 },
						}}
					>
						{/* Check if data exists and has items */}
						{toUse?.data?.length > 0 ? (
							toUse.data.map((item, index) => {
								const isRented = item.status === "RENTED" && viewerInfo === "machines";
								const isTechnician = viewerInfo === "technicians";

								return (
									<FadeInGridItem
										key={item._id}
										item
										xs={12}
										sm={6}
										md={4}
										delay={index * 0.1}
										onClick={() => {
											if (viewerInfo === "machines" || viewerInfo === "clients") {
												const detailPath = detailPathBuilder
													? detailPathBuilder(item)
													: `${item._id}`;
												navigate(detailPath);
											}
										}}
										sx={{
											cursor: (viewerInfo === "machines" || viewerInfo === "clients") ? "pointer" : "default",
										}}
									>
										<StyledCard isRented={isRented}>
											{/* Card Content Area */}
											<Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
												{/* Display image if available */}
												{fields?.some(field => field.type === "image") && fields.find(field => field.type === "image") && item[fields.find(field => field.type === "image").fieldName] ? (
													<Box
														sx={{
															width: "100%",
															height: 180,
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															overflow: "hidden",
															borderRadius: 2,
															mb: 3,
															backgroundColor: "#f3f3f3",
														}}
													>
														<img
															src={item[fields.find(field => field.type === "image").fieldName]}
															alt={item.name || "Preview"}
															style={{
																width: "100%",
																height: "100%",
																objectFit: "cover",
																transition: "transform 0.3s ease",
															}}
															loading="lazy"
															onError={(e) => {
																e.target.onerror = null;
																e.target.src = "/logo192.png";
															}}
														/>
													</Box>
												) : (
													<Box
														sx={{
															width: 80,
															height: 80,
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															backgroundColor: alpha(theme.palette.primary.main, 0.1),
															borderRadius: "50%",
															mb: 3,
														}}
													>
														{getIconForViewer()}
													</Box>
												)}

												{/* Status Chip for machines */}
								{viewerInfo === "machines" && (
													<Chip
														label={getStatusText(item.status)}
														color={item.status === "RENTED" ? "warning" : "success"}
														size="small"
														sx={{
															position: 'absolute',
															top: 12,
															right: 12,
															fontWeight: 600,
														}}
													/>
												)}

												{/* Item details */}
												<Stack spacing={1.5} alignItems="center" width="100%">
													{fields?.map((field, idx) => {
														if (field.type === "image") return "";
														const displayValue = formatDisplayValue(item[field.fieldName]);

														return (
															<Typography key={idx} variant="body2" color="text.secondary" textAlign="center">
																{displayValue}
															</Typography>
														);
													})}

													{/* Status indicator for machines */}
													{viewerInfo === "machines" && (
														<Stack
															direction="row"
															spacing={1}
															alignItems="center"
															justifyContent="center"
															sx={{ mt: 2 }}
														>
															<PulsateCircle color={getStatusColor(item.status)} />
															<Typography variant="body2" color="text.secondary">
																{item.status === "RENTED" ? t("STATUS.IN_USE") : t("STATUS.AVAILABLE")}
															</Typography>
														</Stack>
													)}
												</Stack>
											</Box>

											{/* Technician-specific actions */}
											{isTechnician && (
												<Box sx={{
													p: 2,
													pt: 0,
													display: 'flex',
													justifyContent: 'center',
													gap: 2,
													mt: 'auto'
												}}>
													<Button
														variant="outlined"
														color="primary"
														size="small"
														startIcon={<KeyIcon />}
														onClick={(e) => {
															e.stopPropagation();
															setSelectedTechnician(item._id);
														}}
														sx={{
															borderRadius: 2,
															px: 2,
															py: 1,
															borderWidth: 1.5,
															'&:hover': {
																borderWidth: 1.5,
															}
														}}
													>
														{t("VIEWER.PASSWORD")}
													</Button>
													<Button
														variant="outlined"
														color="error"
														size="small"
														startIcon={<DeleteIcon />}
														onClick={(e) => {
															e.stopPropagation();
															setCurrentItemId(item._id);
															setConfirmDelete(true);
														}}
														sx={{
															borderRadius: 2,
															px: 2,
															py: 1,
															borderWidth: 1.5,
															'&:hover': {
																borderWidth: 1.5,
															}
														}}
													>
														{t("ACTIONS.DELETE")}
													</Button>
												</Box>
											)}
											{viewerInfo === "clients" && (
												<Box sx={{
													p: 2,
													pt: 0,
													display: 'flex',
													justifyContent: 'center',
													mt: 'auto'
												}}>
													<Button
														variant="outlined"
														color="primary"
														size="small"
														startIcon={<EditIcon />}
														onClick={(e) => {
															e.stopPropagation();
															openClientEditDialog(item);
														}}
														sx={{
															borderRadius: 2,
															px: 2,
															py: 1,
															borderWidth: 1.5,
															'&:hover': {
																borderWidth: 1.5,
															}
														}}
													>
														{t("ACTIONS.EDIT")}
													</Button>
												</Box>
											)}
										</StyledCard>
									</FadeInGridItem>
								);
							})
						) : (
							<Grid item xs={12}>
								<Paper
									elevation={1}
									sx={{
										p: 6,
										textAlign: 'center',
										borderRadius: 3,
										backgroundColor: alpha(theme.palette.background.paper, 0.7),
										border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
									}}
								>
									<Box
										sx={{
											width: 100,
											height: 100,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											borderRadius: '50%',
											bgcolor: alpha(theme.palette.info.light, 0.1),
											color: theme.palette.info.main,
											mx: 'auto',
											mb: 3,
										}}
									>
										{getIconForViewer()}
									</Box>
									<Typography variant="h5" color="text.primary" fontWeight={600} gutterBottom>
										{t("VIEWER.NO_ITEM_FOUND")}
									</Typography>
									<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
										{term ? (
											t("VIEWER.SEARCH_EMPTY", { term })
										) : (
											t("VIEWER.EMPTY_COLLECTION")
										)}
									</Typography>
									<Stack direction="row" spacing={2} justifyContent="center">
										{term && (
											<Button
												variant="outlined"
												color="primary"
												startIcon={<RefreshIcon />}
												onClick={() => {
													setValue('');
													setTerm('');
												}}
												sx={{
													borderRadius: 2,
													px: 3,
													py: 1.5,
												}}
											>
												{t("ACTIONS.CLEAR_SEARCH")}
											</Button>
										)}
										<Button
											variant="contained"
											color="primary"
											startIcon={<AddIcon />}
											onClick={() => navigate(`/create?type=${viewerInfo}`)}
											sx={{
												borderRadius: 2,
												px: 3,
												py: 1.5,
												boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
											}}
											>
											{t("ACTIONS.ADD_ENTITY", { entity: t(`PATH.${viewerInfo}`) })}
										</Button>
									</Stack>
								</Paper>
							</Grid>
						)}
					</Grid>

					{/* Pagination */}
					{toUse?.data?.length > 0 && toUse.paginantionInfo?.numberDocuments > limit && (
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
							<Paper
								elevation={2}
								sx={{
									p: 2,
									borderRadius: 3,
									backgroundColor: alpha(theme.palette.background.paper, 0.9),
								}}
							>
								<Pagination
									count={Math.ceil(toUse.paginantionInfo.numberDocuments / limit)}
									onChange={handleChangePage}
									shape="rounded"
									color="primary"
									page={offSet + 1}
									sx={{
										'& .MuiPaginationItem-root': {
											fontWeight: 500,
											borderRadius: 1.5,
											mx: 0.5,
										},
										'& .Mui-selected': {
											boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
										},
									}}
								/>
							</Paper>
						</Box>
					)}

					{/* Back to Top Button */}
					{scrollPosition > 300 && (
						<Fab
							color="primary"
							size="small"
							aria-label={t("ACTIONS.BACK")}
							onClick={() => {
								window.scrollTo({
									top: 0,
									behavior: 'smooth',
								});
							}}
							sx={{
								position: 'fixed',
								bottom: 90,
								right: 30,
								boxShadow: theme.shadows[4],
							}}
						>
							<ArrowUpwardIcon />
						</Fab>
					)}

					{/* Edit Password Dialog */}
					<Dialog
						open={!!selectedTechnician}
						onClose={() => !isSubmitting && setSelectedTechnician(null)}
						PaperProps={{
							sx: {
								borderRadius: 3,
								maxWidth: '400px',
							},
						}}
					>
						<DialogTitle sx={{
							bgcolor: alpha(theme.palette.primary.main, 0.05),
							borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
							py: 2.5,
						}}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<LockIcon color="primary" />
								<Typography variant="h6" fontWeight={600}>
									{t("VIEWER.EDIT_TECH_PASSWORD")}
								</Typography>
							</Stack>
						</DialogTitle>
						<DialogContent sx={{ p: 3, pt: 3 }}>
							<DialogContentText paragraph>
								{t("VIEWER.EDIT_TECH_PASSWORD_DESC")}
							</DialogContentText>
							<TextField
								autoFocus
								margin="dense"
								label={t("VIEWER.NEW_PASSWORD")}
								fullWidth
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								type="password"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<KeyIcon color="action" />
										</InputAdornment>
									),
								}}
							/>
						</DialogContent>
						<DialogActions sx={{ px: 3, py: 2 }}>
							<Button
								onClick={() => setSelectedTechnician(null)}
								disabled={isSubmitting}
							>
								{t("ACTIONS.CANCEL")}
							</Button>
							<Button
								onClick={handleEditPassword}
								color="primary"
								variant="contained"
								disabled={isSubmitting || !newPassword.trim()}
							>
								{t("ACTIONS.SAVE")}
							</Button>
						</DialogActions>
					</Dialog>

					{/* Delete Confirmation Dialog */}
					<Dialog
						open={clientEditOpen}
						onClose={() => !isSubmitting && setClientEditOpen(false)}
						fullWidth
						maxWidth="sm"
					>
						<DialogTitle>{t("VIEWER.EDIT_CLIENT")}</DialogTitle>
						<DialogContent>
							<Stack spacing={2} sx={{ mt: 1 }}>
								<TextField
									label={t("FIELDS.name")}
									value={clientForm.name}
									onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.email")}
									value={clientForm.email}
									onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.phone")}
									value={clientForm.phoneNumber}
									onChange={(e) => setClientForm({ ...clientForm, phoneNumber: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.nif")}
									value={clientForm.nif}
									onChange={(e) => setClientForm({ ...clientForm, nif: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.address")}
									value={clientForm.address}
									onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.postalCode")}
									value={clientForm.postalCode}
									onChange={(e) => setClientForm({ ...clientForm, postalCode: e.target.value })}
									fullWidth
								/>
								<TextField
									label={t("FIELDS.localidade")}
									value={clientForm.localidade}
									onChange={(e) => setClientForm({ ...clientForm, localidade: e.target.value })}
									fullWidth
								/>
							</Stack>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setClientEditOpen(false)} disabled={isSubmitting}>
								{t("ACTIONS.CANCEL")}
							</Button>
							<Button
								onClick={handleSaveClient}
								variant="contained"
								disabled={isSubmitting}
								startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
							>
								{t("ACTIONS.SAVE")}
							</Button>
						</DialogActions>
					</Dialog>
					<Dialog
						open={confirmDelete}
						onClose={() => !isSubmitting && setConfirmDelete(false)}
						PaperProps={{
							sx: {
								borderRadius: 3,
								maxWidth: '400px',
							},
						}}
					>
						<DialogTitle sx={{
							bgcolor: alpha(theme.palette.error.main, 0.05),
							borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
							py: 2.5,
						}}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<WarningIcon color="error" />
								<Typography variant="h6" fontWeight={600}>
									{t("VIEWER.CONFIRM_DELETE")}
								</Typography>
							</Stack>
						</DialogTitle>
						<DialogContent sx={{ p: 3, pt: 3 }}>
							<DialogContentText paragraph>
								{t("VIEWER.DELETE_TECH_CONFIRM")}
							</DialogContentText>
						</DialogContent>
						<DialogActions sx={{ px: 3, py: 2 }}>
							<Button
								onClick={() => setConfirmDelete(false)}
								disabled={isSubmitting}
							>
								{t("ACTIONS.CANCEL")}
							</Button>
							<Button
								onClick={() => handleDeleteTechnician(currentItemId)}
								color="error"
								variant="contained"
								disabled={isSubmitting}
								startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
							>
								{isSubmitting ? t("ACTIONS.DELETING") : t("ACTIONS.DELETE")}
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</Container>
	);
};

export default Viewer;
